import { useState, useEffect, useRef, FormEvent } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { getCurrentUser, signIn, signInWithRedirect, signOut } from 'aws-amplify/auth';
import type { SignInInput } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import './AuthenticatorStyles.css';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { getSigningApiUrl, getSigningAwsAccount, getSsoEnabled, getSsoOnly, getTenantId, setLoginStorage } from '../amplify-config';
import AccedoLogo from '../components/AccedoLogo';

/**
 * Which login mode is currently displayed.
 *
 * - `magic-link` — passwordless email-link flow (default)
 * - `password` — Amplify Authenticator with email + password
 * - `sso` — SAML hosted-UI redirect; only available when the tenant has
 *   `samlMetadata` in its TenantConfiguration. (#2291)
 */
type LoginMode = 'magic-link' | 'password' | 'sso';

/**
 * Sentinel substring thrown by the per-tenant PreAuthentication Cognito
 * trigger when a tenant is SSO-only (#2588). Cognito returns it verbatim
 * inside the NotAuthorizedException message, so the login page can detect it
 * and route the user to the SSO button. Must stay in sync with the message in
 * `services/user/src/handlers/cognito-trigger/pre-authentication/lambda.ts`.
 */
const SSO_ONLY_REJECTION_SENTINEL = 'Password sign-in is disabled';

/**
 * Builds the localStorage key used to remember which login method
 * (`'password'` vs `'magic-link'`) the user last used for a given email.
 * The email is normalized (trim + lowercase) so the key matches regardless of
 * whitespace or case the user typed. (#1944)
 */
function loginMethodKey(email: string): string {
    return 'login-method:' + email.trim().toLowerCase();
}

/**
 * Derives the workspace headline from the current hostname. On a tenant
 * subdomain (e.g. `acme.yesaccedo.com`), returns "Sign in to Acme" — the
 * first subdomain label, title-cased. On the bare branding-site domain
 * (`yesaccedo.com` or `www.yesaccedo.com`) and on localhost / IP-only
 * hosts, falls back to "Welcome to Accedo" so the brand-managed login
 * surface stays generic. (#2049 — gives multi-workspace users a
 * confirmable workspace claim at the moment of sign-in.)
 */
function getLoginHeading(): string {
    if (typeof window === 'undefined') return 'Welcome to Accedo';
    const hostname = window.location.hostname;
    const labels = hostname.split('.');
    // Bare apex domain (e.g. "yesaccedo.com"), or "www" alias, or
    // localhost / IPv4-only: no tenant subdomain to surface.
    if (labels.length < 3) return 'Welcome to Accedo';
    const subdomain = labels[0];
    if (!subdomain || subdomain === 'www' || subdomain === 'localhost') {
        return 'Welcome to Accedo';
    }
    const titleCased = subdomain.charAt(0).toUpperCase() + subdomain.slice(1).toLowerCase();
    return `Sign in to ${titleCased}`;
}

/**
 * The main login page.
 *
 * Default view: passwordless magic-link form (email input + Continue button).
 * Users can toggle to the password-based Amplify Authenticator via a link.
 * Anti-enumeration: always shows success after submitting the magic-link form,
 * regardless of whether the email exists or the API call succeeds.
 */
function LoginPage() {
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { sessionExpired?: boolean } | null;
    const sessionExpired = locationState?.sessionExpired === true;

    const [mode, setMode] = useState<LoginMode>('magic-link');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    // "Stay signed in on this device" — defaults to TRUE. This is an admin tool;
    // logging users out when they close the tab is a surprising default and was a
    // real source of "why do I keep getting logged out" confusion. Users who want
    // session-only tokens can uncheck it. When false, Amplify is switched to
    // sessionStorage at sign-in time so tokens are dropped when the tab/window
    // closes. (#1944; default flipped on per user request.)
    const [rememberMe, setRememberMe] = useState(true);
    // Set when the server-side PreAuthentication trigger rejects a password
    // sign-in because the tenant is SSO-only (#2588). The Cognito client
    // surfaces the trigger's thrown message verbatim inside the
    // NotAuthorizedException; we detect the sentinel substring and route the
    // user to the SSO button instead of leaving them with Amplify's generic
    // "incorrect username or password" framing.
    const [ssoOnlyRejected, setSsoOnlyRejected] = useState(false);

    // The Amplify Authenticator wires `services.handleSignIn` into its xstate
    // machine on mount and re-uses that captured closure on every sign-in —
    // it does NOT refresh per render. Reading `rememberMe` directly from
    // state inside handleSignIn would therefore always observe the mount-time
    // value (`true`) and ignore later checkbox toggles. Route the read
    // through a ref kept in sync by an effect so the captured closure can
    // observe the current value at click time. (#1986)
    const rememberMeRef = useRef(rememberMe);
    useEffect(() => {
        rememberMeRef.current = rememberMe;
    }, [rememberMe]);

    // Tracks whether the user has explicitly toggled the mode via the
    // alt-method link this session. When they have, we stop seeding the mode
    // from the per-email localStorage preference so an explicit choice can
    // win over a stale stored preference until the page is refreshed. (#1944)
    const userToggledModeRef = useRef(false);

    const tenantId = getTenantId();
    // Whether SAML SSO is configured for this tenant — drives whether the
    // "Sign in with single sign-on" button is rendered. Tenants without SAML
    // see the page unchanged from today. (#2291)
    const ssoEnabled = getSsoEnabled();
    // Whether the admin has enforced SSO-only login (#2298). When true
    // together with `ssoEnabled`, the LoginPage auto-redirects to the IdP on
    // mount and hides the password/magic-link form entirely.
    const ssoOnly = getSsoOnly();
    // Loop-guard for the auto-redirect (#2298). Cognito's hosted UI writes
    // `?error=<code>` (+ optional `?error_description=`) when SAML federation
    // fails — Amplify v6's `completeOAuthFlow.js` is the only consumer of
    // these params. We MUST suppress the auto-redirect when the param is
    // present, otherwise a misconfigured IdP traps the user in a redirect
    // loop. Computed outside the effect so it can sit cleanly in the deps
    // array.
    const oauthSearchParams = new URLSearchParams(location.search);
    const hasOAuthError = oauthSearchParams.get('error') !== null;
    const oauthErrorDescription = oauthSearchParams.get('error_description');

    useEffect(() => {
        if (authStatus === 'unauthenticated') {
            getCurrentUser()
                .then(() => navigate('/dashboard', { replace: true }))
                .catch(() => {
                    /* no valid session — stay on login */
                });
        }
    }, [authStatus, navigate]);

    /**
     * SSO-only auto-redirect (#2298). When the admin has enforced SSO-only
     * login for this tenant, send the user straight to the IdP on mount —
     * no password/magic-link form. Loop-guarded by `hasOAuthError`: if
     * Cognito redirected us back here with an `?error=` param, the IdP
     * federation failed; we surface an error + "Try again" button instead
     * of looping. `handleSsoSignIn` is intentionally NOT in the dep array
     * (it's a new closure on every render — would re-fire the redirect
     * indefinitely); the booleans capture everything that should drive
     * the effect.
     */
    useEffect(() => {
        if (!ssoEnabled || !ssoOnly) return;
        if (hasOAuthError) return;
        void signInWithRedirect({ provider: { custom: 'SAML' } }).catch(() => {
            // Logging would PII-leak; the next round trip surfaces the IdP's
            // own UX or sets `?error=` so we land in the loop-guard branch.
        });
    }, [ssoEnabled, ssoOnly, hasOAuthError]);

    /**
     * Seeds the login mode from the per-email last-used preference in
     * localStorage. Runs whenever the email changes (debounced naturally by
     * React batching). Skipped once the user has explicitly toggled the mode
     * via the alt-method link this session. (#1944)
     */
    useEffect(() => {
        if (userToggledModeRef.current) {
            return;
        }
        const trimmed = email.trim();
        if (!trimmed) {
            return;
        }
        let stored: string | null = null;
        try {
            stored = window.localStorage.getItem(loginMethodKey(trimmed));
        } catch {
            /* storage unavailable (private mode etc.) — treat as no stored preference (#2761) */
        }
        // Restore the user's last-used method. `'sso'` is only honored when
        // the tenant actually has SSO configured this session — otherwise
        // we ignore the stored value rather than dead-ending into an
        // unavailable mode. (#2291)
        if (stored === 'password' || stored === 'magic-link') {
            setMode(stored);
        } else if (stored === 'sso' && ssoEnabled) {
            setMode('sso');
        }
    }, [email, ssoEnabled]);

    /**
     * Handles an explicit alt-method click: records the choice in localStorage
     * (so the next visit defaults to it) and flips the override so the
     * email-seeded effect stops touching the mode this session. (#1944)
     */
    function handleModeToggle(next: LoginMode) {
        userToggledModeRef.current = true;
        const trimmed = email.trim();
        if (trimmed) {
            try {
                window.localStorage.setItem(loginMethodKey(trimmed), next);
            } catch {
                /* private mode (Safari etc.) — best-effort write; the toggle must still work (#2761) */
            }
        }
        setMode(next);
    }

    async function handleSignIn(input: SignInInput) {
        // Single source of truth for post-sign-in navigation: the declarative
        // `<Navigate to="/dashboard" replace />` rendered when authStatus
        // flips to 'authenticated'. We MUST NOT call `navigate(...)` here —
        // doing so creates a dual-navigation race with the Amplify-driven
        // re-render of <Navigate>, leaving the URL stuck at /login while
        // dashboard content renders (issues #994 / #1499 / #1761).
        try {
            // Switch Amplify's token storage BEFORE signIn so the issued
            // tokens are persisted to the correct storage backend. Read
            // via the ref so the Authenticator's mount-time closure capture
            // sees the latest checkbox value, not the stale mount value. (#1986)
            setLoginStorage(rememberMeRef.current);
            const result = await signIn(input);
            return result;
        } catch (error) {
            // SSO-only enforcement (#2588): the per-tenant PreAuthentication
            // trigger throws when the tenant is ssoOnly; Cognito wraps that
            // message inside the NotAuthorizedException it returns to the
            // client. Detect the sentinel substring and surface an SSO-routing
            // alert. We still re-throw so the Authenticator clears its
            // submitting state; the alert is rendered alongside it.
            if (error instanceof Error && error.message.includes(SSO_ONLY_REJECTION_SENTINEL)) {
                setSsoOnlyRejected(true);
            }
            if (error instanceof Error && error.message.includes('There is already a signed in user')) {
                try {
                    await getCurrentUser();
                    // Already signed in — let the Amplify-driven authStatus
                    // flip + declarative <Navigate> handle the redirect.
                    return { isSignedIn: true, nextStep: { signInStep: 'DONE' as const } };
                } catch {
                    await signOut();
                    throw error;
                }
            }
            throw error;
        }
    }

    /**
     * Starts the SAML SSO redirect flow. Persists `'sso'` as the per-email
     * last-used method so a returning user lands directly on this affordance
     * next time, then asks Amplify to redirect to Cognito's hosted UI which
     * forwards to the tenant's IdP. After the IdP authenticates the user,
     * Cognito redirects back to `${origin}` with `?code=&state=`; Amplify v6
     * intercepts those params transparently, exchanges them for tokens, and
     * flips `authStatus` to `'authenticated'` — which causes the existing
     * `<Navigate to="/dashboard" replace />` below to fire. (#2291)
     */
    async function handleSsoSignIn() {
        handleModeToggle('sso');
        try {
            await signInWithRedirect({ provider: { custom: 'SAML' } });
        } catch {
            // Failure here typically means Amplify config is missing (oauth
            // block) or Cognito rejected the redirect. The user stays on
            // /login and can retry; logging would PII-leak the email if we
            // included it, so we stay silent and rely on the IdP's own UX.
        }
    }

    async function handleMagicLinkSubmit(e: FormEvent) {
        e.preventDefault();
        setSubmitting(true);
        // Record magic-link as the last-used method for this email so the next
        // visit defaults to it, and persist the rememberMe choice so the
        // separate /login/:token page load can read it back when it issues
        // tokens (we can't set Amplify storage here — no tokens are issued by
        // this fetch). setLoginStorage(rememberMe) is also called here as a
        // no-op on this page load, kept for symmetry with the password path
        // and because it's harmless. (#1944)
        const trimmed = email.trim();
        if (trimmed) {
            try {
                window.localStorage.setItem(loginMethodKey(trimmed), 'magic-link');
            } catch {
                /* private mode (Safari etc.) — best-effort write; don't block the login-link request */
            }
        }
        // Persist the rememberMe choice across the magic-link page-load gap.
        // LoginByToken reads this key, calls setLoginStorage(...) before signIn,
        // then clears it. Wrapped in try/catch for private-mode resilience —
        // the reader defaults to `false` (privacy-respecting) when missing.
        try {
            window.localStorage.setItem('login-remember-me', rememberMe ? 'true' : 'false');
        } catch {
            /* private mode — fall back to default (false) on read */
        }
        setLoginStorage(rememberMe);
        try {
            await fetch(`${getSigningApiUrl()}/auth/login-link`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Aws-Account': getSigningAwsAccount(),
                },
                body: JSON.stringify({ email, tenantId }),
            });
        } catch {
            // Swallow errors — always show success to prevent enumeration
        } finally {
            setSubmitting(false);
            setSubmitted(true);
        }
    }

    if (authStatus === 'configuring') {
        return (
            <div className="loading-container">
                <CircularProgress size={32} />
                <p>Loading...</p>
            </div>
        );
    }

    // If user is already authenticated, redirect to dashboard
    if (authStatus === 'authenticated') {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-header">
                    <div className="brand-logo" data-testid="brand-logo">
                        <AccedoLogo size={40} />
                        Accedo
                    </div>
                    <h1>{getLoginHeading()}</h1>
                    <p>Sign in to access your policy management dashboard</p>
                </div>

                {sessionExpired && (
                    <Alert severity="info" role="status" sx={{ mb: 2 }}>
                        <strong>Your session has expired.</strong> If you are a signer, please click a new magic link from your email to sign in again.
                        Administrators can sign in below.
                    </Alert>
                )}

                {ssoEnabled && ssoOnly ? (
                    <Box className="login-form" sx={{ mt: 3, textAlign: 'center' }}>
                        {hasOAuthError ? (
                            <>
                                {/* SSO-only error path (#2298). The auto-redirect
                                    is suppressed when the URL carries an
                                    `?error=` param (loop-guard); surface the
                                    failure to the admin and offer a manual
                                    retry. The raw OAuth code (e.g.
                                    `server_error`, `access_denied`) is
                                    intentionally NOT shown — we only surface
                                    `error_description` when present. */}
                                <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                                    <strong>Sign-in failed.</strong>{' '}
                                    {oauthErrorDescription ? oauthErrorDescription : 'Your identity provider rejected the sign-in attempt.'}
                                </Alert>
                                <Button
                                    type="button"
                                    variant="contained"
                                    fullWidth
                                    onClick={() =>
                                        void signInWithRedirect({
                                            provider: { custom: 'SAML' },
                                        }).catch(() => {
                                            /* swallow — surfaces via IdP UX or another ?error= */
                                        })
                                    }
                                    sx={{ py: 1.5, fontSize: '1rem' }}
                                >
                                    Try again
                                </Button>
                            </>
                        ) : (
                            // Splash shown while the auto-redirect effect kicks
                            // off. On a healthy SAML round trip the user only
                            // sees this for a fraction of a second before the
                            // hosted-UI navigation commits.
                            <>
                                <CircularProgress size={32} sx={{ mb: 2 }} />
                                <Typography variant="body1">Redirecting to your identity provider…</Typography>
                            </>
                        )}
                    </Box>
                ) : mode === 'sso' ? (
                    <>
                        <Box className="login-form" sx={{ mt: 3, textAlign: 'center' }}>
                            {/* SAML SSO mode (#2291). Single primary action —
                                the IdP collects credentials, so no form fields
                                here. Click → Amplify v6 `signInWithRedirect`
                                kicks off the hosted-UI round trip. */}
                            <Button type="button" variant="contained" fullWidth onClick={handleSsoSignIn} sx={{ py: 1.5, fontSize: '1rem' }}>
                                Sign in with single sign-on
                            </Button>
                        </Box>

                        <Box
                            component="hr"
                            className="login-divider"
                            sx={{
                                mt: 2,
                                mb: 0,
                                border: 0,
                                borderTop: '1px solid var(--color-border)',
                            }}
                        />
                        <Box className="login-footer" sx={{ mt: 1, textAlign: 'center' }}>
                            <Button
                                type="button"
                                variant="text"
                                data-variant="alt-method"
                                onClick={() => handleModeToggle('magic-link')}
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: 'primary.main',
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        backgroundColor: 'transparent',
                                        color: 'primary.dark',
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                Sign in with email link instead
                            </Button>
                        </Box>
                    </>
                ) : mode === 'magic-link' ? (
                    <>
                        {submitted ? (
                            <Alert severity="info" role="status" sx={{ my: 2 }}>
                                <strong>Check your email</strong> for a sign-in link. The link expires in 15 minutes.
                            </Alert>
                        ) : (
                            <Box component="form" onSubmit={handleMagicLinkSubmit} className="login-form" sx={{ mt: 3 }}>
                                <TextField
                                    id="magic-link-email"
                                    label="Email"
                                    type="email"
                                    required
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    sx={{ mb: 2 }}
                                    slotProps={{
                                        htmlInput: {
                                            'aria-label': 'Email',
                                            // Mobile-keyboard hints so the @ key is on the
                                            // first row, autocomplete pulls in the saved email,
                                            // and the OS doesn't auto-capitalize / spellcheck. (#1280)
                                            inputMode: 'email',
                                            autoCapitalize: 'none',
                                            autoComplete: 'email',
                                            spellCheck: false,
                                        },
                                    }}
                                />
                                {/* "Stay signed in on this device" \u2014 unchecked
                                    by default. Checked \u2192 Amplify persists tokens
                                    to localStorage (30-day Cognito refresh).
                                    Unchecked \u2192 sessionStorage (session-only). (#1944)
                                    Placed ABOVE the submit button (#2050) so users
                                    encounter the control in the natural form-fill
                                    sequence; previously it sat below the primary
                                    CTA and most users never noticed it. */}
                                <FormControlLabel
                                    sx={{ mb: 1 }}
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            slotProps={{ input: { 'aria-label': 'Stay signed in on this device' } }}
                                        />
                                    }
                                    label="Stay signed in on this device"
                                />
                                <Button type="submit" variant="contained" fullWidth disabled={submitting} sx={{ py: 1.5, fontSize: '1rem' }}>
                                    {submitting ? 'Sending\u2026' : 'Send Sign-In Link'}
                                </Button>
                            </Box>
                        )}

                        {/* Signer-steering note (#2848). The backend mints
                            login links ONLY for signers (#853) and returns the
                            same generic 200 for every email (anti-enumeration),
                            so the UI cannot know whether a typed email belongs
                            to an admin. This copy is therefore STATIC — shown
                            to everyone, before and after submit, never varied
                            per email — and steers admins to the password link
                            below instead of leaving them waiting for an email
                            that will never arrive. */}
                        <Typography data-testid="magic-link-signer-note" variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                            Magic-link sign-in is for policy signers. Administrators sign in with a password.
                        </Typography>

                        {/* Secondary action styled as a true link — teal
                            brand color with underline (#1801, ux-manifest
                            § Design decisions #1). The filled teal CTA above
                            is visually distinct enough that the link doesn't
                            need to be muted to avoid competing with it. */}
                        <Box
                            component="hr"
                            className="login-divider"
                            sx={{
                                mt: 2,
                                mb: 0,
                                border: 0,
                                borderTop: '1px solid var(--color-border)',
                            }}
                        />
                        <Box className="login-footer" sx={{ mt: 1, textAlign: 'center' }}>
                            <Button
                                type="button"
                                variant="text"
                                data-variant="alt-method"
                                data-testid="password-mode-toggle"
                                onClick={() => handleModeToggle('password')}
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: 'primary.main',
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        backgroundColor: 'transparent',
                                        color: 'primary.dark',
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                Or sign in with password
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <div className="login-form">
                            {/* "Stay signed in on this device" — same defaults
                                and storage semantics as the magic-link form's
                                checkbox. Placed ABOVE the Authenticator (#2050)
                                so users encounter the control before the
                                Sign-In CTA. Read at sign-in time by
                                handleSignIn() (via rememberMeRef) to switch
                                Amplify's token storage backend. (#1944) */}
                            <FormControlLabel
                                sx={{ mb: 1 }}
                                control={
                                    <Checkbox
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        slotProps={{ input: { 'aria-label': 'Stay signed in on this device' } }}
                                    />
                                }
                                label="Stay signed in on this device"
                            />
                            {/* SSO-only rejection routing (#2588). The
                                PreAuthentication trigger blocked this password
                                sign-in because the tenant requires federated
                                SSO. Route the user to the SSO button rather
                                than leaving them with Amplify's generic
                                "incorrect username or password" message. */}
                            {ssoOnlyRejected && (
                                <Alert
                                    severity="warning"
                                    role="alert"
                                    data-testid="sso-only-rejection"
                                    sx={{ mb: 2 }}
                                    action={
                                        ssoEnabled ? (
                                            <Button color="inherit" size="small" type="button" onClick={handleSsoSignIn}>
                                                Sign in with single sign-on
                                            </Button>
                                        ) : undefined
                                    }
                                >
                                    Password sign-in is disabled for this workspace. Use single sign-on to continue.
                                </Alert>
                            )}
                            <Authenticator
                                hideSignUp={true}
                                services={{ handleSignIn }}
                                formFields={{
                                    signIn: {
                                        username: {
                                            label: 'Email',
                                            placeholder: 'you@company.com',
                                        },
                                    },
                                }}
                            />
                        </div>

                        {/* Mirror the magic-link alt-method link styling
                            (#1801) so password-mode users get the same
                            link-styled affordance. */}
                        <Box
                            component="hr"
                            className="login-divider"
                            sx={{
                                mt: 2,
                                mb: 0,
                                border: 0,
                                borderTop: '1px solid var(--color-border)',
                            }}
                        />
                        <Box className="login-footer" sx={{ mt: 1, textAlign: 'center' }}>
                            <Button
                                type="button"
                                variant="text"
                                data-variant="alt-method"
                                onClick={() => handleModeToggle('magic-link')}
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: 'primary.main',
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        backgroundColor: 'transparent',
                                        color: 'primary.dark',
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid',
                                        outlineColor: 'primary.main',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                Sign in with email link instead
                            </Button>
                        </Box>
                    </>
                )}

                {/* SSO discoverability surface (#2291). When the tenant has
                    SAML configured, the magic-link / password modes show a
                    secondary "Sign in with single sign-on" affordance below
                    the form so users can pivot to SSO without first having
                    to discover the per-email mode toggle. In `sso` mode the
                    primary CTA above already IS the SSO button, so this
                    block hides itself to avoid duplication.

                    (#2298) Also hidden when `ssoOnly` is on — the splash
                    above is the only affordance in that state. */}
                {ssoEnabled && !ssoOnly && mode !== 'sso' && (
                    <Box className="login-sso" sx={{ mt: 2, textAlign: 'center' }}>
                        <Button type="button" variant="outlined" fullWidth onClick={handleSsoSignIn} sx={{ py: 1.25, fontSize: '0.9375rem' }}>
                            Sign in with single sign-on
                        </Button>
                    </Box>
                )}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link to="/" style={{ color: 'var(--color-accent-link, #0D9488)', fontSize: '0.9rem' }}>
                        &larr; Back to home
                    </Link>
                </Box>
            </div>
        </div>
    );
}

export default LoginPage;
